import React from 'react';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/styles/makeStyles';
import withStyles from '@material-ui/styles/withStyles';
import _ from 'lodash';
import { Trans, useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import FeeTableRow from './FeeTableRow';
import Tooltip from '../../common/Tooltip';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import { amountFormatter } from './fees.helper';
import { CURRENCY_POSITION_TYPE } from '../../../shared/enums/FeeCellType';
import { selectVersion } from '../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../shared/utils/versionUtils';

const useStyles = makeStyles(theme => ({
  tableBoxContainer: {
    [theme.breakpoints.down('lg')]: {
      width: 'auto',
      overflowX: 'scroll',
    },
  },
  tableContainer: {
    background: 'white',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '1.5rem',
  },
  columnTitle: {
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: '#677289',
    whiteSpace: 'nowrap',
  },
  columnTooltip: {
    verticalAlign: 'bottom',
  },
  total: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: '1rem',
  },
  totalText: {
    marginRight: '1rem',
  },
  price: {
    marginRight: '1.8rem',
  },
}));

const StyledTable = withStyles({
  root: {
    border: 'none',
  },
})(Table);

const StyledTableCell = withStyles({
  root: {
    borderRight: 'none',
    backgroundColor: 'inherit',
  },
})(TableCell);

export default function FeeTable({ id, uiSchema, feesSelection, formData, disabled, total, ...restProps }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const _feesSelection = feesSelection || uiSchema[uiSchemaProperties.UI_DATA];
  const enabledCategories = uiSchema[uiSchemaProperties.UI_ENABLED_CATEGORIES];
  const { disabledFeeCodes = [], hiddenFeeCodes = [] } = uiSchema[uiSchemaProperties.UI_OPTIONS] || {};
  const selectedVersion = useSelector(selectVersion);

  let feeValues = _feesSelection?.values.filter(
    _fee =>
      hiddenFeeCodes.length === 0 ||
      (hiddenFeeCodes?.indexOf(_fee.code) === -1 && hiddenFeeCodes?.indexOf(`${_fee.category}-${_fee.code}`) === -1),
  );

  if (formData?.selectedFees?.length > 0) {
    feeValues = feeValues.map(
      feeValue =>
        _.find(formData?.selectedFees, item => item.code === feeValue?.code && item.hasOwnProperty('showText')) ||
        feeValue,
    );
  }

  const feeTxt = amountFormatter().format(total || 0);
  const feeTextWithCurrency =
    _feesSelection?.currencyPosition === CURRENCY_POSITION_TYPE?.AFTER
      ? `${feeTxt} ${_feesSelection?.currencySymbol}`
      : `${_feesSelection?.currencySymbol} ${feeTxt}`;

  // Filter columns to exclude hidden ones
  const visibleColumns = _feesSelection?.columns?.filter(({ hidden = false }) => !hidden);

  return (
    <div id={id} className={classes.tableContainer}>
      <Box className={classes.tableBoxContainer}>
        <StyledTable>
          <TableHead>
            <TableRow>
              {visibleColumns?.map(({ title, tooltip }) => (
                <StyledTableCell key={title}>
                  <Typography className={classes.columnTitle}>
                    {title}
                    {title && tooltip && (typeof tooltip === 'string' || tooltip.value) && (
                      <Tooltip className={classes.columnTooltip} title={<Trans i18nKey={tooltip.value || tooltip} />} />
                    )}
                  </Typography>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {feeValues?.map((_fee) => {
              const isDisabled =
                disabled ||
                _fee.disabled ||
                !!disabledFeeCodes?.find(__fee => __fee === _fee.code) ||
                (_fee.category && !enabledCategories?.includes(_fee.category));

              return (
                <FeeTableRow
                  key={_fee.code}
                  classes={classes}
                  fee={_fee}
                  feesSelection={{
                    ..._feesSelection,
                    columns: visibleColumns,
                  }} // Pass only visible columns
                  formData={formData}
                  uiSchema={uiSchema}
                  disabled={isDisabled}
                  {...restProps}
                />
              );
            })}
          </TableBody>
        </StyledTable>
      </Box>
      <Grid container justifyContent="flex-end">
        <Typography
          className={classes.total}
          {...dataTestId({
            value: `feesTableTotal-NP--${selectedVersion.value}`,
          })}
        >
          <span className={classes.totalText}>{t('fees.total')}</span>
          <span className={classes.price}>{feeTextWithCurrency}</span>
        </Typography>
      </Grid>
    </div>
  );
}
